import { useEffect, useRef } from "react";
import { Field, getIn, setIn } from "formik";
import Select from "../Select";
import { Flex } from "../Flex";
import { Label } from "../Label";
import { useScreeningProvider } from "../../data/useScreeningProvider";

const yesNoOptions = [
  { label: "Yes", value: true },
  { label: "No", value: false },
];

interface MonitoringEventsFieldsProps {
  /** The Formik render props object from the surrounding form. */
  formikProps: any;
  /** Field name prefix, for forms that hold monitoring events in an array. */
  namePrefix?: string;
  pepLabel?: string;
  adverseLabel?: string;
  /** Layout props spread onto each row, so each caller keeps its own spacing. */
  rowProps?: any;
  /**
   * Copy introducing the selects, rendered above them. It lives here rather
   * than in the caller so it disappears with the selects under LexisNexis2,
   * where there is nothing for the user to select.
   */
  intro?: React.ReactNode;
  /**
   * Called whenever these values change, including when the LexisNexis2
   * defaults are applied. Forms that gate their submit button on the user
   * having answered need to hear about both cases, or a LexisNexis2 user is
   * left with a button that never enables.
   */
  onChange?: () => void;
}

/**
 * The two monitoring event selects, for a legacy tenant only.
 *
 * `pep_and_sanction_required` and `adverse_media_required` exist so the legacy
 * provider knows which checks to run. A LexisNexis Screening check returns PEP,
 * sanctions and adverse media in one payload, and neither boolean is read on the
 * LexisNexis2 request or response path, so there is nothing to ask: creating the
 * monitoring record is itself the instruction.
 *
 * Under LexisNexis2 this renders nothing and sets both booleans to true. They
 * are still written because the monitoring PDF and the external API report them
 * outward, and a record that monitors everything should not report that it
 * monitors nothing.
 */
export const MonitoringEventsFields = ({
  formikProps,
  namePrefix = "",
  pepLabel = "PEP & sanctions monitoring",
  adverseLabel = "Adverse media monitoring",
  rowProps = {},
  intro,
  onChange,
}: MonitoringEventsFieldsProps) => {
  const { isLexisNexis2, isLoading } = useScreeningProvider();

  const pepField = `${namePrefix}pep_and_sanction_required`;
  const adverseField = `${namePrefix}adverse_media_required`;

  const { setFieldValue, setValues } = formikProps;
  const hasSetDefaults = useRef(false);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (isLoading || !isLexisNexis2 || hasSetDefaults.current) return;
    hasSetDefaults.current = true;
    // Set both fields in one update. Two sequential setFieldValue calls would
    // each validate against a snapshot missing the other's pending change,
    // leaving the form permanently invalid with no visible field to fix it.
    setValues((values: any) => setIn(setIn(values, pepField, true), adverseField, true));
    onChangeRef.current?.();
  }, [isLoading, isLexisNexis2, pepField, adverseField, setValues]);

  // Render nothing until the provider is known, so a LexisNexis2 user never sees
  // selects they should not be answering.
  if (isLoading || isLexisNexis2) return null;

  const renderSelect = (name: string) => (
    <Field name={name}>
      {({ field, meta }: any) => (
        <Select
          {...field}
          onBlur={() => formikProps.setFieldTouched(field.name)}
          onChange={(option: any) => {
            setFieldValue(name, option.value);
            onChange?.();
          }}
          placeholder="Select value"
          name={name}
          value={yesNoOptions.find(
            ({ value }) => value === getIn(formikProps.values, name)
          )}
          options={yesNoOptions}
          isRequired
          width={"50%"}
          hasError={
            meta.touched && (getIn(formikProps.errors, name) || !!meta.error)
          }
          errorMessage={
            meta.touched
              ? getIn(formikProps.errors, name) || meta.error
              : ""
          }
        />
      )}
    </Field>
  );

  return (
    <>
      {intro}

      <Flex {...rowProps} alignItems="center" justifyContent="space-between">
        <Label>{pepLabel}</Label>
        {renderSelect(pepField)}
      </Flex>

      <Flex {...rowProps} alignItems="center" justifyContent="space-between">
        <Label>{adverseLabel}</Label>
        {renderSelect(adverseField)}
      </Flex>
    </>
  );
};
